/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Main bulk operations hook - composes all domain-specific hooks
 */

"use client";

import { useCallback, useState } from "react";
import type { BulkOperationDefinition, BulkOperationStatus } from "./types";
import { useBulkOperationExecution } from "./use-bulk-operation-execution";
import { useBulkOperationsAvailable } from "./use-bulk-operations-available";
import { useBulkProgress } from "./use-bulk-progress";
import { useBulkSelection } from "./use-bulk-selection";
import { useBulkUndo } from "./use-bulk-undo";

export interface UseBulkOperationsProps<T> {
  items: T[];
  keyExtractor: (item: T) => string;
  operations?: BulkOperationDefinition<T>[];
  onBulkDelete?: (ids: string[]) => Promise<void>;
  onBulkExport?: (items: T[]) => Promise<void>;
  onSelectionChange?: (ids: Set<string>, items: T[]) => void;
  maxUndoHistory?: number;
  defaultUndoTimeLimit?: number;
  onOperationComplete?: (
    operationId: string,
    status: BulkOperationStatus,
    summary: {
      completed: number;
      failed: number;
      failures: Array<{ itemId: string; error: string; timestamp: Date }>;
      duration: number;
    },
  ) => void;
  onUndo?: (undoState: import("./types").UndoState<T>) => Promise<void>;
}

export interface UseBulkOperationsReturn<T> {
  selectedIds: Set<string>;
  selectedCount: number;
  selectedItems: T[];
  availableOperations: BulkOperationDefinition<T>[];
  bulkProgress: import("./types").BulkProgressState;
  isOperationInProgress: boolean;
  undoHistory: import("./types").UndoState<T>[];
  currentUndo: import("./types").UndoState<T> | null;
  canUndo: boolean;
  selectAll: () => void;
  clearSelection: () => void;
  toggleItem: (id: string) => void;
  setSelection: (ids: Set<string>) => void;
  executeOperation: (operationId: string) => Promise<void>;
  pauseOperation: () => void;
  resumeOperation: () => void;
  cancelOperation: () => void;
  undoLastOperation: () => Promise<void>;
  undoOperation: (operationId: string) => Promise<void>;
  clearUndoHistory: () => void;
  executeBulkDelete: () => Promise<void>;
  executeBulkExport: () => Promise<void>;
}

/**
 * Custom hook for managing bulk operations
 */
export function useBulkOperations<T>({
  items,
  keyExtractor,
  operations = [],
  onBulkDelete,
  onBulkExport,
  onSelectionChange,
  maxUndoHistory = 10,
  defaultUndoTimeLimit = 300, // 5 minutes
  onOperationComplete,
  onUndo,
}: UseBulkOperationsProps<T>): UseBulkOperationsReturn<T> {
  // Selection management
  const {
    selectedIds,
    setSelectedIds,
    selectedItems,
    selectedCount,
    selectAll,
    clearSelection,
    toggleItem,
    setSelection,
  } = useBulkSelection({
    items,
    keyExtractor,
    onSelectionChange,
  });

  // Progress tracking
  const { bulkProgress, setBulkProgress, isOperationInProgress } =
    useBulkProgress();

  // Undo management
  const {
    undoHistory,
    currentUndo,
    canUndo,
    addUndoOperation,
    undoOperation,
    undoLastOperation,
    clearUndoHistory,
  } = useBulkUndo({
    maxUndoHistory,
    defaultUndoTimeLimit,
    onUndo,
  });

  // Available operations
  const { availableOperations } = useBulkOperationsAvailable({
    operations,
    keyExtractor,
    onBulkDelete,
    onBulkExport,
  });

  // Operation execution (needs isPaused state)
  const [isPaused, setIsPaused] = useState(false);
  const { executeOperation, pauseOperation, resumeOperation, cancelOperation } =
    useBulkOperationExecution({
      availableOperations,
      selectedItems,
      keyExtractor,
      bulkProgress,
      setBulkProgress,
      isPaused,
      setIsPaused,
      setSelectedIds,
      onSelectionChange,
      onOperationComplete,
      addUndoOperation,
    });

  // Legacy execute bulk delete
  const executeBulkDelete = useCallback(async () => {
    await executeOperation("bulk-delete");
  }, [executeOperation]);

  // Legacy execute bulk export
  const executeBulkExport = useCallback(async () => {
    await executeOperation("bulk-export");
  }, [executeOperation]);

  return {
    selectedIds,
    selectedCount,
    selectedItems,
    availableOperations,
    bulkProgress,
    isOperationInProgress,
    undoHistory,
    currentUndo,
    canUndo,
    selectAll,
    clearSelection,
    toggleItem,
    setSelection,
    executeOperation,
    pauseOperation,
    resumeOperation,
    cancelOperation,
    undoLastOperation,
    undoOperation,
    clearUndoHistory,
    executeBulkDelete,
    executeBulkExport,
  };
}

// Re-export types for convenience
export type {
  BulkOperationDefinition,
  BulkOperationMode,
  BulkOperationStatus,
  BulkProgressState,
  UndoState,
} from "./types";
